// 261005 E1-P1 — hàm THUẦN của khung 5 chặng. KHÔNG import antd / react (jest của react-scripts không load được antd v6).
// Mọi quyết định "đi đâu / hiện gì / hỏi gì" nằm ở đây để test được; hook và component chỉ là lớp mỏng.
import type {
  WorkbenchEpisode, WorkbenchFailedJob, WorkbenchJobKind, WorkbenchNextAction, WorkbenchReason, WorkbenchRunningJob,
  WorkbenchStage, WorkbenchStageKey, WorkbenchView, HandoffPlanPreview,
} from '../../../types/adaptationWorkbench';
import { formatJobStepProgress } from './jobStepProgress';
import { friendlyJobNote, friendlyReasonText } from './jobErrorText';
import { REJUDGE_JOB_LABEL, REJUDGE_LABEL, isRejudgeJob } from './rejudgeView';

export const STAGE_ORDER: WorkbenchStageKey[] = ['source', 'skeleton', 'localize', 'split', 'script'];
export const STAGE_LABEL: Record<WorkbenchStageKey, string> = {
  source: 'Nguồn', skeleton: 'Khung xương', localize: 'Bản địa hoá', split: 'Chia tập', script: 'Kịch bản',
};
export const STAGE_NUMBER: Record<WorkbenchStageKey, string> = { source: '①', skeleton: '②', localize: '③', split: '④', script: '⑤' };

// ─── Chặng: dấu trạng thái ────────────────────────────────────────────────

export type MarkTone = 'ok' | 'warn' | 'run' | 'lock' | 'todo';
export interface StageMarkView { icon: string; text: string; tone: MarkTone; tip: string; attention: boolean }

export const stageMark = (stage: WorkbenchStage): StageMarkView => {
  const attentionTip = friendlyReasonText(stage.attention[0]?.message);
  const attention = stage.attention.length > 0;
  const base = ((): Omit<StageMarkView, 'attention'> => {
    switch (stage.state) {
      case 'running': return { icon: '⏳', text: 'Đang chạy', tone: 'run', tip: 'Đang có việc chạy nền ở bước này' };
      case 'blocked': {
        const n = stage.blockers.length;
        return { icon: '●', text: n > 0 ? String(n) : '', tone: 'warn', tip: friendlyReasonText(stage.blockers[0]?.message) || 'Đang bị chặn' };
      }
      case 'todo': return { icon: '○', text: '', tone: 'todo', tip: 'Chưa làm' };
      case 'locked': return { icon: '🔒', text: '', tone: 'lock', tip: 'Cần xong bước trước' };
      default: return { icon: '✓', text: '', tone: 'ok', tip: 'Xong' };
    }
  })();
  return { ...base, attention, tip: attention && stage.state !== 'blocked' ? attentionTip : (attention ? `${base.tip} · ${attentionTip}` : base.tip) };
};

/** "Chặng này đang khoá: …" — chặng sớm nhất chưa `done` đứng trước chặng đang xem. */
export const lockReason = (key: WorkbenchStageKey, stages: WorkbenchStage[]): string => {
  const idx = STAGE_ORDER.indexOf(key);
  for (let i = 0; i < idx; i++) {
    const s = stages.find((x) => x.key === STAGE_ORDER[i]);
    if (s && s.state !== 'done') return `cần xong bước ${STAGE_NUMBER[s.key]} ${STAGE_LABEL[s.key]} trước.`;
  }
  return 'cần xong bước trước.';
};

// ─── Đích của "Việc tiếp theo" / "Đi tới" / "Xem" vi phạm ─────────────────────

export type WorkbenchIntent =
  | 'scan' | 'addChapter' | 'buildSkeleton' | 'approve' | 'localize' | 'split' | 'focusEpisode'
  | 'openEpisode' | 'markEpisode' | 'writeMissing' | 'handoff' | 'seasonReader' | 'focusEntities'
  /** 261015 P4c-1: mở tab "Tổng quan truyện" của ② (nextAction `OVERVIEW_MISSING` / `OVERVIEW_NEEDS_FIX`). */
  | 'openOverview';

export interface WorkbenchTarget {
  stage: WorkbenchStageKey; episode?: number; intent?: WorkbenchIntent;
  /** 261005 E1-P3a — `focusEntities`: mở bảng Nhân vật/Bối cảnh của ②, tuỳ chọn tô một dòng / lọc "Chưa có tên". */
  entityId?: string; entityKind?: 'character' | 'location'; entityFilter?: 'missing';
}

type EpisodeLike = Pick<WorkbenchEpisode, 'ep' | 'written'>;

const isSeasonReaderCode = (code: string): boolean =>
  code.startsWith('SEASON_READER_') || code === 'STEP_GATE_BLOCKED' || code === 'RANGE_HANDOFF_NOT_READY';

/** `stage` lấy THẲNG từ backend (không tự map lại). Tập `failed` ⇒ chỉ tô hàng (không có kịch bản để "Xem"). */
export const nextActionTarget = (next: WorkbenchNextAction | null | undefined, episodes?: EpisodeLike[]): WorkbenchTarget | null => {
  if (!next) return null;
  const t: WorkbenchTarget = { stage: next.stage };
  if (next.episode !== undefined) t.episode = next.episode;
  switch (next.kind) {
    case 'watch_job': case 'job_failed': return t;
    case 'review_episode': {
      const failed = episodes?.find((e) => e.ep === next.episode)?.written === 'failed';
      t.intent = failed ? 'markEpisode' : 'openEpisode';
      return t;
    }
    case 'handoff': t.intent = 'handoff'; return t;
    case 'resolve_blocker': {
      if (next.code === 'SCAN_NEEDED') t.intent = 'scan';
      else if (next.code === 'SKELETON_NOT_APPROVED') t.intent = 'approve';
      else if (next.code === 'OVERVIEW_MISSING' || next.code === 'OVERVIEW_NEEDS_FIX') t.intent = 'openOverview';   // 261015 P4c-1
      else if (next.code === 'EPISODES_STALE') t.intent = 'split';
      else if (next.code === 'EPISODE_PLAN_NOT_APPROVED') t.intent = 'split';
      else if (next.code === 'NAMES_MISSING') t.intent = 'localize';
      else if (next.code === 'CRITIC_UNRESOLVED' && next.episode !== undefined) t.intent = 'openEpisode';   // 261015 P5d (D2): mở tập đầu còn lỗi nặng
      else if (next.code === 'SOURCE_NAME_UNRESOLVED' && next.episode !== undefined) t.intent = 'openEpisode';   // 261023 E2-P2: mở tập đầu còn tên gốc không thay được
      else if (isSeasonReaderCode(next.code)) t.intent = 'seasonReader';
      return t;
    }
    case 'pipeline_step': {
      const m: Partial<Record<WorkbenchStageKey, WorkbenchIntent>> = {
        source: 'addChapter', skeleton: 'buildSkeleton', split: 'split', script: 'writeMissing',
      };
      if (m[next.stage]) t.intent = m[next.stage];
      return t;
    }
    default: return t;
  }
};

/** Nút "Đi tới" của khối "Cần bạn xem" (blocker / attention của một chặng). */
export const reasonTarget = (stage: WorkbenchStageKey, reason: WorkbenchReason, episodes?: EpisodeLike[]): WorkbenchTarget => {
  const t: WorkbenchTarget = { stage };
  if (reason.episode !== undefined) t.episode = reason.episode;
  switch (reason.code) {
    case 'SCAN_NEEDED': t.stage = 'source'; t.intent = 'scan'; break;
    case 'SKELETON_NOT_APPROVED': t.stage = 'skeleton'; t.intent = 'approve'; break;
    case 'OVERVIEW_MISSING': case 'OVERVIEW_NEEDS_FIX': t.stage = 'skeleton'; t.intent = 'openOverview'; break;   // 261015 P4c-1
    case 'NAMES_MISSING': t.stage = 'localize'; t.intent = 'localize'; break;
    case 'EPISODES_STALE': t.stage = 'split'; t.intent = 'split'; break;
    case 'EPISODE_REVIEW': {
      t.stage = 'script';
      const failed = episodes?.find((e) => e.ep === reason.episode)?.written === 'failed';
      if (reason.episode !== undefined) t.intent = failed ? 'markEpisode' : 'openEpisode';
      break;
    }
    case 'WORLD_CHANGED': t.stage = 'script'; if (reason.episode !== undefined) t.intent = 'openEpisode'; break;   // E3-P3a
    case 'DANGLING_REFS': t.stage = 'script'; if (reason.episode !== undefined) t.intent = 'openEpisode'; break;   // E3-P1a
    case 'ORPHAN_SCRIPT': t.stage = 'script'; if (reason.episode !== undefined) t.intent = 'markEpisode'; break;
    case 'HANDOFF_MAYBE': t.stage = 'script'; t.intent = 'handoff'; break;
    default:
      if (isSeasonReaderCode(reason.code)) { t.stage = 'script'; t.intent = 'seasonReader'; }
  }
  return t;
};

/** `tong-quan` = tab CŨ (nhãn "Cấu trúc chuyển thể" từ 261015 P4c-1; giữ khoá vì `violationTarget` dựa vào); `tong-quan-truyen` = tab MỚI "Tổng quan truyện". */
export type SkeletonSubTab = 'tong-quan-truyen' | 'tong-quan' | 'nhan-vat' | 'boi-canh' | 'cao-trao' | 'phai-giu';
export interface ViolationTarget { stage: 'skeleton' | 'split'; subTab?: SkeletonSubTab; episode?: number }

interface OutlineIds {
  characters?: Array<{ id: string }>; locations?: Array<{ id: string }>; beats?: Array<{ id: string }>;
}

/** `ref` của vi phạm: id nhân vật / bối cảnh / beat (UUID) hoặc số tập. Không nhận ra ⇒ ở lại Tổng quan. */
export const violationTarget = (ref: string, outline: OutlineIds | null | undefined): ViolationTarget => {
  if (outline?.characters?.some((c) => c.id === ref)) return { stage: 'skeleton', subTab: 'nhan-vat' };
  if (outline?.locations?.some((c) => c.id === ref)) return { stage: 'skeleton', subTab: 'boi-canh' };
  if (outline?.beats?.some((c) => c.id === ref)) return { stage: 'skeleton', subTab: 'cao-trao' };
  if (/^\d+$/.test(ref)) return { stage: 'split', episode: Number(ref) };
  return { stage: 'skeleton', subTab: 'tong-quan' };
};

/** DOM id của phần tử đích (để `useScrollWhenReady` cuộn tới). `undefined` = không cuộn. */
export const intentAnchor = (t: WorkbenchTarget): string | undefined => {
  switch (t.intent) {
    case 'scan': return 'btn-scan';
    case 'addChapter': return 'btn-add-chapter';
    case 'buildSkeleton': return 'section-genre';
    case 'approve': return 'section-approve';
    case 'openOverview': return 'section-overview';
    case 'localize': return 'section-localize';
    case 'writeMissing': return 'btn-write-missing';
    case 'seasonReader': return 'section-season-reader';
    case 'split': return 'section-split-mode';
    case 'focusEntities': return t.entityId ? `outline-ref-${t.entityId}` : 'section-entities';
    case 'focusEpisode': case 'openEpisode': case 'markEpisode':
      return t.episode !== undefined ? `outline-ref-${t.episode}` : undefined;
    default: return undefined;
  }
};

// ─── Dải job ──────────────────────────────────────────────────────────────

const FAIL_LABEL: Record<WorkbenchJobKind, string> = {
  crawl: 'Nạp truyện', scan: 'Quét', skeleton: 'Dựng khung xương', split: 'Chia tập', localize: 'Bản địa hoá',
  write: 'Viết kịch bản', season_reader: 'AI đọc cả mùa', visual: 'Đề xuất nhận dạng', overview: 'Tạo tổng quan truyện', other: 'Tác vụ khác',
};
const RUN_LABEL: Record<WorkbenchJobKind, string> = {
  crawl: 'nạp truyện', scan: 'quét truyện', skeleton: 'dựng khung xương', split: 'chia tập', localize: 'bản địa hoá',
  write: 'viết kịch bản', season_reader: 'AI đọc cả mùa', visual: 'đề xuất nhận dạng', overview: 'tạo tổng quan truyện', other: 'chạy tác vụ khác',
};
export const jobKindLabel = (k: WorkbenchJobKind): string => FAIL_LABEL[k] ?? FAIL_LABEL.other;

const minutesAgo = (iso: string | null | undefined, now: number): number | null => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? Math.max(0, Math.floor((now - t) / 60000)) : null;
};

/**
 * Mảng dòng cho dải đầu trang: LỖI trước, đang chạy sau, mỗi loại tối đa 2 dòng. Không có gì ⇒ `null`.
 * 261005 E4-P2: job có `progress` ⇒ dòng nói bước đang làm / "Lỗi ở bước: …"; `opts.hideRunningJobId` = job đã có khối Alert riêng ở header ⇒ bỏ dòng ⏳ trùng.
 */
export const jobBand = (
  jobs: { running: WorkbenchRunningJob[]; failed: WorkbenchFailedJob[] } | null | undefined,
  now: number = Date.now(),
  opts: { hideRunningJobId?: string | null; hideRunningKinds?: WorkbenchJobKind[] } = {},
): string[] | null => {
  const lines: string[] = [];
  for (const j of (jobs?.failed ?? []).slice(0, 2)) {
    const n = minutesAgo(j.endedAt, now);
    const step = formatJobStepProgress(j.progress);
    const note = friendlyJobNote(j.note);
    lines.push(`⚠ ${isRejudgeJob(j) ? REJUDGE_LABEL : FAIL_LABEL[j.kind] ?? FAIL_LABEL.other} lỗi${n === null ? '' : ` ${n}′ trước`}${step ? `. Lỗi ở bước: ${step}` : ''}${note ? `${step ? ' — ' : ': '}${note}` : ''}`);
  }
  const running = (jobs?.running ?? []).filter((j) => (!opts.hideRunningJobId || j.jobId !== opts.hideRunningJobId) && !(opts.hideRunningKinds ?? []).includes(j.kind));
  for (const j of running.slice(0, 2)) {
    const n = minutesAgo(j.startedAt, now);
    const step = formatJobStepProgress(j.progress);
    lines.push(`⏳ ${isRejudgeJob(j) ? REJUDGE_JOB_LABEL : `Đang ${RUN_LABEL[j.kind] ?? RUN_LABEL.other}`} …${step ? ` ${step}` : ''}${n === null ? '' : ` (${n}′)`}`);
  }
  return lines.length ? lines : null;
};

/** Nhãn "Tiếp theo" cho người đọc: bỏ chú thích mã trong ngoặc BE còn để sót, vd "Bản địa hoá (localize)" → "Bản địa hoá". */
export const nextActionLabel = (label: string): string =>
  String(label ?? '').replace(/\s*\((?:localize|skeleton|split|source|script)\)/gi, '').trim();

export interface StageJobLine { jobId: string; state: 'running' | 'failed'; text: string }

/** 261005 E4-P2 — dòng NHÌN THẤY NGAY ở đầu chặng. Chỉ job có `progress` (crawl/scan/rejudge… không có ⇒ không dòng, giữ như cũ). */
export const stageJobLines = (
  jobs: { running: WorkbenchRunningJob[]; failed: WorkbenchFailedJob[] } | null | undefined,
  stage: WorkbenchStageKey,
): StageJobLine[] => {
  const out: StageJobLine[] = [];
  for (const j of (jobs?.failed ?? []).filter((x) => x.stage === stage).slice(0, 2)) {
    const step = formatJobStepProgress(j.progress);
    if (step) out.push({ jobId: j.jobId, state: 'failed', text: `Lỗi ở bước: ${step}${friendlyJobNote(j.note) ? ` — ${friendlyJobNote(j.note)}` : ''}` });
  }
  for (const j of (jobs?.running ?? []).filter((x) => x.stage === stage).slice(0, 2)) {
    const step = formatJobStepProgress(j.progress);
    if (step) out.push({ jobId: j.jobId, state: 'running', text: `Đang chạy: ${step}` });
  }
  return out;
};

/**
 * 261005 E4-P2 (Q1a) — chữ của Alert "Đang <việc> ở nền" ở đầu trang. `episodes`: lấy tiến độ của job cùng `jobId` trong `workbench.jobs.running`,
 * chưa có thì "Đang chia tập…" (không còn câu giả, không còn "Đang bắt đầu…"). crawl/scan: formatter cũ (`progressText`), chưa có ⇒ "Đang bắt đầu…".
 */
export const bgJobText = (
  key: string,
  progressText: string | null,
  running: WorkbenchRunningJob[] | null | undefined,
  jobId: string,
): string => {
  if (key === 'episodes') return formatJobStepProgress(running?.find((j) => j.jobId === jobId)?.progress) || 'Đang chia tập…';
  return progressText ?? 'Đang bắt đầu…';
};

/** F15 — chữ BE trả khi khởi động job → chữ người đọc: bỏ route nội bộ "— xem ở /adaptation/…" và mã job "· job 0dc2e270…". Dùng cho mọi toast khởi động job. */
export const plainJobMessage = (text: string | null | undefined): string =>
  String(text ?? '')
    .replace(/\s*[—-]\s*xem ở\s+\/\S*/g, '')
    .replace(/\s*·\s*job\s+[0-9a-f-]{6,}…?/gi, '')
    .trim();

/** test-dev F11 — MỘT dòng tiến độ cho job nền của phiên này: "Đang nạp truyện — Đã tải 8/20 chương". Không mã job, không câu "nút bị khoá" (nút đã mờ). */
export const bgJobLine = (key: string, progressText: string | null, running: WorkbenchRunningJob[] | null | undefined, jobId: string): string => {
  const label = key === 'crawl' ? 'nạp truyện' : key === 'scan' ? 'quét truyện' : 'chia tập';
  return `Đang ${label} — ${bgJobText(key, progressText, running, jobId)}`;
};

/** F11 — loại job mà dòng ⏳ ở dải đã được dòng bgJob thay (crawl/scan có `progressText` riêng). */
export const hideBgRunningKinds = (key: string | null | undefined): WorkbenchJobKind[] => (key === 'crawl' ? ['crawl'] : key === 'scan' ? ['scan'] : []);

/** F11 — đang nạp/quét thì KHÔNG nhắc "chưa quét" (nhắc khi chưa nạp xong là sai thời điểm). */
export const withoutScanNudge = <T extends { code: string }>(reasons: T[], jobRunning: boolean): T[] =>
  (jobRunning ? reasons.filter((r) => r.code !== 'SCAN_NEEDED') : reasons);

/** Thấy `running` đi từ >0 về 0 ⇒ tải lại dữ liệu MỘT lần. `prev === null` = chưa có lần đo trước. */
export const shouldReload = (prevRunning: number | null, nextRunning: number): boolean =>
  prevRunning !== null && prevRunning > 0 && nextRunning === 0;

// ─── URL ?stage= ──────────────────────────────────────────────────────────

export const parseStageParam = (v: string | null | undefined): WorkbenchStageKey | null =>
  (STAGE_ORDER as string[]).includes(v ?? '') ? (v as WorkbenchStageKey) : null;

/** Chặng mở lúc đầu: URL hợp lệ → thắng; không thì theo `nextAction.stage`; không có gì → `source`. */
export const resolveInitialStage = (v: string | null | undefined, next: WorkbenchNextAction | null | undefined): WorkbenchStageKey =>
  parseStageParam(v) ?? next?.stage ?? 'source';

// ─── Độ rộng 3 cột ────────────────────────────────────────────────────────

export type WidthKey = 'stages' | 'assistant' | 'list';
export const WIDTH_LIMITS: Record<WidthKey, { min: number; max: number; def: number }> = {
  stages: { min: 64, max: 320, def: 210 },
  assistant: { min: 300, max: 760, def: 400 },
  list: { min: 220, max: 560, def: 320 },
};
export const COMPACT_BELOW = 130;
export const WIDTHS_STORAGE_KEY = 'talebox.adaptation.widths.v1';
export type Widths = Record<WidthKey, number>;

export const defaultWidths = (): Widths => ({ stages: WIDTH_LIMITS.stages.def, assistant: WIDTH_LIMITS.assistant.def, list: WIDTH_LIMITS.list.def });

export const clampWidth = (key: WidthKey, px: number): number => {
  const l = WIDTH_LIMITS[key];
  if (typeof px !== 'number' || !Number.isFinite(px)) return l.def;
  return Math.min(l.max, Math.max(l.min, Math.round(px)));
};
export const isCompactStages = (px: number): boolean => px < COMPACT_BELOW;

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;
const globalStorage = (): StorageLike | null => {
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
};

export const loadWidths = (storage: StorageLike | null = globalStorage()): Widths => {
  const out = defaultWidths();
  try {
    const raw = storage?.getItem(WIDTHS_STORAGE_KEY);
    if (!raw) return out;
    const j = JSON.parse(raw);
    if (!j || typeof j !== 'object') return out;
    (Object.keys(out) as WidthKey[]).forEach((k) => { if (typeof j[k] === 'number') out[k] = clampWidth(k, j[k]); });
  } catch { return defaultWidths(); }
  return out;
};

export const saveWidths = (w: Widths, storage: StorageLike | null = globalStorage()): void => {
  try { storage?.setItem(WIDTHS_STORAGE_KEY, JSON.stringify(w)); } catch { /* không lưu được thì thôi */ }
};

// ─── Phân trang bảng tập ──────────────────────────────────────────────────

export const EPISODE_PAGE_SIZE = 15;
export const pageOfEpisode = (ep: number, ordered: number[], pageSize = EPISODE_PAGE_SIZE): number => {
  const i = ordered.indexOf(ep);
  return i < 0 ? 1 : Math.floor(i / pageSize) + 1;
};

// ─── Chữ hộp hỏi lại ──────────────────────────────────────────────────────

export type DangerAction =
  | 'rebuildSkeleton' | 'unapprove' | 'mergeCharacter' | 'removeEntity' | 'repair' | 'rescan' | 'resplit'
  | 'rewriteEpisode' | 'writeMissing' | 'writeRange';

export interface DangerCtx {
  n?: number; ep?: number; name?: string; versionCount?: number;
  /** `writeRange` (261005 E1-P2): dải a–b, số tập đã viết được giữ, chế độ chia tập. */
  from?: number; to?: number; skipped?: number; splitMode?: 'chapter' | 'range' | null;
}
export interface DangerCopyView { title: string; content: string; okText: string; danger: boolean }

export const dangerCopy = (action: DangerAction, ctx: DangerCtx = {}): DangerCopyView => {
  switch (action) {
    case 'rebuildSkeleton': return {
      title: 'Dựng lại khung xương?', danger: false, okText: 'Dựng lại',
      content: 'Gộp lại khung xương với chương hiện có. Nhân vật / bối cảnh / cao trào cũ được giữ, cái mới thêm vào. Khung xương về bản nháp, phải duyệt lại.',
    };
    case 'unapprove': return {
      title: 'Bỏ duyệt khung xương?', danger: true, okText: 'Bỏ duyệt',
      content: 'Khung xương về bản nháp. Bước sau giữ dữ liệu nhưng phải duyệt lại mới chạy tiếp.',
    };
    case 'mergeCharacter': return {
      title: `Gộp${ctx.name ? ` ${ctx.name}` : ' nhân vật'}?`, danger: true, okText: 'Gộp',
      content: 'Khôi phục được ở mục Phiên bản của Trợ lý AI (bước ②). Mọi chỗ có nhân vật bị gộp sẽ thuộc về nhân vật được giữ. Khung xương về bản nháp.',
    };
    case 'removeEntity': return {
      title: `Bỏ${ctx.name ? ` ${ctx.name}` : ''}?`, danger: true, okText: 'Bỏ',
      content: 'Khung xương về bản nháp.',
    };
    case 'repair': return {
      title: 'Kiểm lại & sửa tự động?', danger: false, okText: 'Chạy',
      content: 'AI sửa lại danh sách tập cho hết lỗi. Chạy nền, tốn AI.',
    };
    case 'rescan': return {
      title: 'Quét lại truyện?', danger: false, okText: 'Chạy',
      content: 'Chạy nền, tốn AI. Kết quả cũ bị thay.',
    };
    case 'resplit': return {
      title: 'Chia lại tập?', danger: false, okText: 'Chạy',
      content: 'Chạy nền, tốn AI. Kết quả cũ bị thay.',
    };
    case 'rewriteEpisode': {
      const ep = ctx.ep ?? 0;
      const restore = (ctx.versionCount ?? 0) > 0
        ? 'Bản cũ vẫn khôi phục được ở Phiên bản (Sửa tập).'
        : 'Tập này chưa có phiên bản lưu: bản hiện tại sẽ mất.';
      return { title: `Viết lại tập ${ep}?`, danger: true, okText: 'Viết lại', content: `Viết lại tập ${ep}, đè bản hiện tại. ${restore} Tốn AI.` };
    }
    case 'writeMissing': return {
      title: 'Viết các tập chưa viết?', danger: false, okText: 'Viết',
      content: `Viết ${ctx.n ?? 0} tập chưa có kịch bản. Chạy nền, tốn AI.`,
    };
    case 'writeRange': {
      const { from = 0, to = 0, n = 0, skipped = 0 } = ctx;
      return {
        title: `Viết các tập chưa viết trong tập ${from}–${to}?`, danger: false, okText: 'Viết',
        content: `Viết ${n} tập chưa có kịch bản trong tập ${from}–${to}.${skipped > 0 ? ` ${skipped} tập đã viết sẽ được giữ nguyên.` : ''}`
          + `${ctx.splitMode === 'range' ? ' Truyện theo đoạn nguồn viết tuần tự, dừng ở tập đầu tiên bị hỏng.' : ''} Chạy nền, tốn AI.`,
      };
    }
    default: return { title: 'Xác nhận?', danger: false, okText: 'Đồng ý', content: '' };
  }
};

// ─── Hộp "Đẩy sang Chapters" ──────────────────────────────────────────────

export interface HandoffSummary {
  created: number; updated: number; keptEdited: number; keptForPlan: number; unchanged: number; total: number;
  leaks: Array<{ episode: number; terms: string[] }>;
  /** 261023 E2-P2: tập sẽ được tự thay tên gốc trong chương (tăng dần, không lặp). */
  autoSwapped: number[];
  /** 261023 E2-P2: tên gốc còn sót không thay được (máy chủ sẽ từ chối đẩy) — mỗi tập một dòng "term ×n". */
  blocked: Array<{ episode: number; terms: string[] }>;
  /** `leaks` trừ term đã nằm trong `blocked` cùng tập (không báo một tên hai lần); gồm cả dòng khối Bank. Tập hết term thì bỏ. */
  warnLeaks: Array<{ episode: number; terms: string[] }>;
}

/** Đếm TỪ `actions` (route chỉ có 3 số created/updated/skipped, không tách lý do giữ). */
export const summarizeHandoffPlan = (plan: HandoffPlanPreview | null | undefined): HandoffSummary => {
  const s: HandoffSummary = { created: 0, updated: 0, keptEdited: 0, keptForPlan: 0, unchanged: 0, total: 0, leaks: [], autoSwapped: [], blocked: [], warnLeaks: [] };
  for (const a of plan?.actions ?? []) {
    s.total++;
    if (a.action === 'create') s.created++;
    else if (a.action === 'update') s.updated++;
    else if (a.action === 'keptEdited') s.keptEdited++;
    else if (a.action === 'keptForPlan') s.keptForPlan++;
    else s.unchanged++;
  }
  s.leaks = (plan?.leaks ?? []).map((l) => ({ episode: l.episode, terms: l.terms.map((t) => `${t.term} ×${t.count}`) }));
  s.autoSwapped = Array.from(new Set((plan?.autoSwap ?? []).map((a) => a.episode))).sort((a, b) => a - b);
  const block = (plan?.sourceNameBlock ?? []).filter((b) => b && Array.isArray(b.terms) && b.terms.length);
  s.blocked = block.map((b) => ({ episode: b.episode, terms: b.terms.map((t) => `${t.term} ×${t.count}`) }));
  const blockedTerms = new Map(block.map((b) => [b.episode, new Set(b.terms.map((t) => t.term))]));
  s.warnLeaks = (plan?.leaks ?? [])
    .map((l) => ({ episode: l.episode, terms: l.terms.filter((t) => !blockedTerms.get(l.episode)?.has(t.term)).map((t) => `${t.term} ×${t.count}`) }))
    .filter((l) => l.terms.length > 0);
  return s;
};

/** Có thật sự đổi gì ở Chapters không (để hộp báo "không có gì để đẩy"). */
export const handoffHasWork = (s: HandoffSummary): boolean => s.created + s.updated > 0;

export type HandoffClick = 'NOTHING' | 'MODAL' | 'PUSH';

/**
 * 261015 P5a (D7/C47) — bấm "Đẩy sang Chapters…" (đã GET kế hoạch) làm gì. THỨ TỰ CHỐT (261023 E2-P2):
 * không plan ⇒ `NOTHING` (không đẩy mù) → có tên gốc không thay được (`blocked`) ⇒ `MODAL` (kể cả 0 việc: người cần thấy vì sao; nút Đẩy tắt) →
 * không có việc ⇒ `NOTHING` (toast) → còn tên gốc rò (`warnLeaks`, gồm khối Bank) ⇒ `MODAL` (người đọc rồi quyết, như cũ) → còn lại `PUSH` (đẩy thẳng — C3 bỏ dừng thừa; chỉ có tự thay tên cũng đẩy thẳng).
 */
export const handoffClickDecision = (plan: HandoffPlanPreview | null | undefined): HandoffClick => {
  if (!plan) return 'NOTHING';
  const s = summarizeHandoffPlan(plan);
  if (s.blocked.length > 0) return 'MODAL';
  if (!handoffHasWork(s)) return 'NOTHING';
  return s.warnLeaks.length > 0 ? 'MODAL' : 'PUSH';
};

/** Toast khi `NOTHING`: nói rõ vì sao 0 việc (giữ nguyên N tập: sửa tay / có Film plan / không đổi) thay vì hộp "Không có tập nào…". */
export const handoffNothingText = (plan: HandoffPlanPreview | null | undefined): string => {
  const s = summarizeHandoffPlan(plan);
  const kept = s.keptEdited + s.keptForPlan + s.unchanged;
  const base = 'Không có tập nào cần tạo hay cập nhật';
  if (kept === 0) return `${base}.`;
  const why = [s.keptEdited && `${s.keptEdited} sửa tay`, s.keptForPlan && `${s.keptForPlan} có Film plan`, s.unchanged && `${s.unchanged} không đổi`].filter(Boolean).join(' · ');
  return `${base} — giữ nguyên ${kept} tập (${why}).`;
};

// ─── 261005 E5: số đếm phụ bên phải mục chặng ở thanh trái ───────────────────
/** Nguồn = số chương nguồn · Chia tập = số tập · Kịch bản = đã viết/số tập. Chặng khác rỗng. */
export const stageCounts = (w: WorkbenchView | null | undefined): Partial<Record<WorkbenchStageKey, string>> => {
  if (!w) return {};
  const f = w.outlineFlags;
  const out: Partial<Record<WorkbenchStageKey, string>> = {};
  if (f.rawChapters > 0) out.source = String(f.rawChapters);
  if (f.episodesListed > 0) {
    out.split = String(f.episodesListed);
    out.script = `${w.episodes.filter((e) => e.written === 'draft').length}/${f.episodesListed}`;
  }
  return out;
};
